/**
 * The journey, as popularised by the Éowyn Challenge (2003), which took its
 * mileage from Karen Wynn Fonstad's "The Atlas of Middle-earth".
 *
 * Distances are in miles. `startMile` is cumulative from Bag End.
 * The outbound quest (legs 1-4) totals 1,779 miles. After Mount Doom the
 * Eagles carry Frodo and Sam to Minas Tirith; that flight is a zero-mile
 * transition, matching the Éowyn Challenge charts.
 */
export interface Segment {
  id: string;
  order: number;
  name: string;
  from: string;
  to: string;
  miles: number;
  startMile: number;
  endMile: number;
  /** Who the walker is following on this leg. */
  company: string;
  /** Short flavour line for the segment card. */
  blurb: string;
}

const RAW: Array<Omit<Segment, "startMile" | "endMile" | "order">> = [
  {
    id: "shire-to-rivendell",
    name: "The Shire to Rivendell",
    from: "Bag End",
    to: "Rivendell",
    miles: 458,
    company: "Frodo, Sam, Merry and Pippin",
    blurb: "Out of the Shire, through the Old Forest and Bree, past Weathertop to the Ford of Bruinen.",
  },
  {
    id: "rivendell-to-lothlorien",
    name: "Rivendell to Lothlórien",
    from: "Rivendell",
    to: "Caras Galadhon",
    miles: 462,
    company: "The Fellowship",
    blurb: "South through Hollin, the failed crossing of Caradhras, the dark of Moria, and into the Golden Wood.",
  },
  {
    id: "lothlorien-to-rauros",
    name: "Lothlórien to Rauros",
    from: "Caras Galadhon",
    to: "Parth Galen",
    miles: 389,
    company: "The Fellowship",
    blurb: "Down the Great River Anduin by boat, past the Argonath to the falls of Rauros.",
  },
  {
    id: "rauros-to-mount-doom",
    name: "Rauros to Mount Doom",
    from: "Parth Galen",
    to: "Sammath Naur",
    miles: 470,
    company: "Frodo and Sam (and Gollum)",
    blurb: "The Emyn Muil, the Dead Marshes, Ithilien, Cirith Ungol, and the last crawl across Gorgoroth.",
  },
  {
    id: "minas-tirith-to-isengard",
    name: "Minas Tirith to Isengard",
    from: "Minas Tirith",
    to: "Isengard",
    miles: 535,
    company: "The Fellowship, homeward",
    blurb: "Carried by Eagles to Minas Tirith, then north through Rohan to Edoras, Helm's Deep and Isengard.",
  },
  {
    id: "isengard-to-rivendell",
    name: "Isengard to Rivendell",
    from: "Isengard",
    to: "Rivendell",
    miles: 693,
    company: "The hobbits and Gandalf",
    blurb: "Through the Gap of Rohan, across Dunland and the ruins of Tharbad, back to the Last Homely House.",
  },
  {
    id: "rivendell-to-bag-end",
    name: "Rivendell to Bag End",
    from: "Rivendell",
    to: "Bag End",
    miles: 397,
    company: "The four hobbits",
    blurb: "The East Road home by way of Bree, and the Scouring of the Shire.",
  },
  {
    id: "grey-havens",
    name: "To the Grey Havens and home",
    from: "Bag End",
    to: "Bag End",
    miles: 467,
    company: "Frodo, then Sam alone",
    blurb: "Bonus leg. Ride with Frodo to the Grey Havens, then walk home with Sam. 'Well, I'm back.'",
  },
];

export const SEGMENTS: Segment[] = (() => {
  let cursor = 0;
  return RAW.map((s, i) => {
    const seg: Segment = { ...s, order: i + 1, startMile: cursor, endMile: cursor + s.miles };
    cursor += s.miles;
    return seg;
  });
})();

export const QUEST_MILES = SEGMENTS.filter((s) => s.order <= 4).reduce((a, s) => a + s.miles, 0); // 1779
export const TOTAL_MILES = SEGMENTS.reduce((a, s) => a + s.miles, 0); // 3871
